import { useParams, Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import ScrollToTop from "@/components/base/ScrollToTop";
import Breadcrumb from "@/components/base/Breadcrumb";
import SchemaOrg from "@/components/base/SchemaOrg";
import { articleMap, articles as allArticles } from "@/mocks/articles";
import { categoryMap } from "@/mocks/categories";
import { useTranslation } from "react-i18next";

export default function Article() {
  const { slug } = useParams<{ slug: string }>();
  const { t } = useTranslation("article");

  const article = slug ? articleMap[slug] : undefined;

  if (!article) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-20 pb-20">
          <div className="w-full px-4 md:px-6 max-w-7xl mx-auto text-center">
            <h1 className="font-heading text-2xl font-semibold text-foreground-900 mb-4">文章未找到</h1>
            <Link to="/" className="text-primary-600 hover:text-primary-700 cursor-pointer">返回首页</Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const category = categoryMap[article.category];
  const relatedArticles = allArticles
    .filter((a) => a.category === article.category && a.slug !== article.slug)
    .slice(0, 3);
  const currentIndex = allArticles.findIndex((a) => a.slug === article.slug);
  const prevArticle = currentIndex > 0 ? allArticles[currentIndex - 1] : null;
  const nextArticle = currentIndex < allArticles.length - 1 ? allArticles[currentIndex + 1] : null;

  const schemaArticle = {
    "@type": "Article",
    headline: article.title,
    description: article.excerpt,
    image: article.image,
    datePublished: article.date,
    author: {
      "@type": "Person",
      name: article.author,
    },
    publisher: {
      "@type": "Organization",
      name: "华丽的外出",
    },
    inLanguage: "zh-CN",
  };

  return (
    <>
      <ScrollToTop />
      <SchemaOrg type="Article" data={schemaArticle} />
      <SchemaOrg
        type="BreadcrumbList"
        data={{
          itemListElement: [
            {
              "@type": "ListItem",
              position: 1,
              name: "首页",
              item: "https://www.hualidewaichu.com",
            },
            {
              "@type": "ListItem",
              position: 2,
              name: category?.name || "分类",
              item: `https://www.hualidewaichu.com/category/${article.category}`,
            },
            {
              "@type": "ListItem",
              position: 3,
              name: article.title,
            },
          ],
        }}
      />
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-20">
          <article className="w-full px-4 md:px-6 max-w-4xl mx-auto pb-16">
            <Breadcrumb
              items={[
                { label: t("breadcrumbHome"), href: "/" },
                { label: category?.name || "分类", href: `/category/${article.category}` },
                { label: article.title },
              ]}
            />

            <div className="mb-8">
              <div className="flex items-center gap-3 mb-4">
                {category && (
                  <Link
                    to={`/category/${article.category}`}
                    className="px-3 py-1 bg-accent-100 text-accent-700 text-xs font-medium rounded-full cursor-pointer hover:bg-accent-200 transition-colors"
                  >
                    {category.name}
                  </Link>
                )}
                <span className="text-xs text-foreground-400">{article.date}</span>
                <span className="text-xs text-foreground-400">·</span>
                <span className="text-xs text-foreground-400">{article.readingTime} 分钟阅读</span>
              </div>

              <h1 className="font-heading text-2xl md:text-3xl lg:text-4xl font-semibold text-foreground-900 mb-4 leading-tight">
                {article.title}
              </h1>

              <div className="flex items-center gap-3 mb-6">
                <div className="w-8 h-8 rounded-full bg-secondary-100 flex items-center justify-center">
                  <i className="ri-user-line text-secondary-600 text-sm"></i>
                </div>
                <span className="text-sm text-foreground-600 font-medium">{article.author}</span>
              </div>
            </div>

            <div className="mb-10 rounded-lg overflow-hidden">
              <img
                src={article.image}
                alt={article.title}
                className="w-full h-auto object-cover"
                title={article.title}
              />
            </div>

            <div
              className="prose max-w-none"
              dangerouslySetInnerHTML={{ __html: article.content }}
            />

            <div className="mt-10 pt-8 border-t border-background-200">
              <div className="flex flex-wrap gap-2 mb-8">
                {article.tags.map((tag) => (
                  <span
                    key={tag}
                    className="px-3 py-1.5 bg-background-100 text-foreground-600 text-xs rounded-md"
                  >
                    #{tag}
                  </span>
                ))}
              </div>

              <div className="flex items-center justify-between">
                {prevArticle ? (
                  <Link
                    to={`/article/${prevArticle.slug}`}
                    className="flex items-center gap-2 text-sm text-foreground-600 hover:text-primary-600 transition-colors cursor-pointer"
                  >
                    <i className="ri-arrow-left-line"></i>
                    <span className="hidden sm:inline">{prevArticle.title}</span>
                    <span className="sm:hidden">上一篇</span>
                  </Link>
                ) : (
                  <div></div>
                )}
                {nextArticle ? (
                  <Link
                    to={`/article/${nextArticle.slug}`}
                    className="flex items-center gap-2 text-sm text-foreground-600 hover:text-primary-600 transition-colors cursor-pointer"
                  >
                    <span className="hidden sm:inline">{nextArticle.title}</span>
                    <span className="sm:hidden">下一篇</span>
                    <i className="ri-arrow-right-line"></i>
                  </Link>
                ) : (
                  <div></div>
                )}
              </div>
            </div>

            {relatedArticles.length > 0 && (
              <div className="mt-14">
                <h2 className="font-heading text-xl font-semibold text-foreground-900 mb-6">
                  {t("relatedArticles")}
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
                  {relatedArticles.map((ra) => (
                    <Link
                      key={ra.slug}
                      to={`/article/${ra.slug}`}
                      className="group bg-background-100 rounded-lg overflow-hidden cursor-pointer transition-all duration-300"
                    >
                      <div className="h-36 overflow-hidden">
                        <img
                          src={ra.image}
                          alt={ra.title}
                          className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                        />
                      </div>
                      <div className="p-3">
                        <h3 className="font-heading text-sm font-semibold text-foreground-900 line-clamp-2 group-hover:text-primary-600 transition-colors">
                          {ra.title}
                        </h3>
                        <p className="text-xs text-foreground-400 mt-1">{ra.date}</p>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </article>
        </main>
        <Footer />
      </div>
    </>
  );
}